import { post, get, put, patch } from './api-client';
import { useGlobalStore } from '@atlas/shared-state';
import type { Permission, FieldPermission, BranchContext, BranchInfo } from '@atlas/shared-types';
import { getMenuLayout } from './preferences-service';

// ── Types matching backend DTOs ─────────────────────────────────

interface LoginRequest {
  email: string;
  password: string;
}

interface AuthResponse {
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: string;
  user?: UserInfo;
  accountLocked?: boolean;
  passwordExpired?: boolean;
}

export type LoginResult =
  | { status: 'ok' }
  | { status: 'account_locked' }
  | { status: 'password_expired' };

interface UserInfo {
  id: string;
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
  companyId: string | null;
  roles: string[];
  permissions: string[];
  currentBranchId?: string;
  currentBranchCode?: string;
  currentBranchName?: string;
  primaryBranchId?: string;
  hasAllBranches: boolean;
  effectiveBranches: BranchInfo[];
}

// ── Storage Keys ────────────────────────────────────────────────

const REFRESH_TOKEN_KEY = 'atlas_refresh_token';

// ── Auth Service ────────────────────────────────────────────────

function parseAllPermissions(permStrings: string[]): {
  resourcePermissions: Permission[];
  fieldPermissions: FieldPermission[];
} {
  const resourcePermissions: Permission[] = [];
  const fieldPermissions: FieldPermission[] = [];

  for (const p of permStrings) {
    const [module, resourceOrField, action] = p.split(':');
    if (!module || !resourceOrField || !action) continue;

    // Field-level permissions have a dot: "hr:employees.Salary:read"
    const dotIndex = resourceOrField.indexOf('.');
    if (dotIndex === -1) {
      resourcePermissions.push({
        module,
        resource: resourceOrField,
        action,
      });
    } else {
      const resource = resourceOrField.substring(0, dotIndex);
      const field = resourceOrField.substring(dotIndex + 1);
      fieldPermissions.push({ module, resource, field, action });
    }
  }

  return { resourcePermissions, fieldPermissions };
}

function setAuthState(response: AuthResponse & { accessToken: string; refreshToken: string; expiresAt: string; user: UserInfo }) {
  const store = useGlobalStore.getState();
  const { resourcePermissions, fieldPermissions } = parseAllPermissions(
    response.user.permissions
  );

  store.setUser({
    id: response.user.id,
    email: response.user.email,
    firstName: response.user.firstName,
    lastName: response.user.lastName,
    displayName: response.user.displayName,
    isActive: true,
    companyId: response.user.companyId ?? '',
    roles: response.user.roles,
    permissions: resourcePermissions,
    currentBranchId: response.user.currentBranchId,
    currentBranchCode: response.user.currentBranchCode,
    currentBranchName: response.user.currentBranchName,
    primaryBranchId: response.user.primaryBranchId,
    hasAllBranches: response.user.hasAllBranches,
    effectiveBranches: response.user.effectiveBranches,
  });

  store.setPermissions(resourcePermissions);
  store.setFieldPermissions(fieldPermissions);
  store.setAccessToken(response.accessToken);
  store.setLoading(false);

  // Set branch context from auth response
  const currentBranchFromEffective = response.user.currentBranchId
    ? response.user.effectiveBranches.find(b => b.id === response.user.currentBranchId)
    : null;
  const branchContext: BranchContext = {
    currentBranch: response.user.currentBranchId
      ? {
          id: response.user.currentBranchId,
          code: response.user.currentBranchCode ?? '',
          name: response.user.currentBranchName ?? '',
          isHeadOffice: currentBranchFromEffective?.isHeadOffice ?? false,
        }
      : null,
    primaryBranchId: response.user.primaryBranchId ?? null,
    effectiveBranches: response.user.effectiveBranches,
    hasAllBranches: response.user.hasAllBranches,
  };
  store.setBranchContext(branchContext);

  // Store refresh token for session restore
  localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);

  // Fire-and-forget: load user menu layout
  getMenuLayout().then((layout) => {
    useGlobalStore.getState().setMenuLayout(layout);
  }).catch(() => { /* ignore — menu will use default */ });

  // Fire-and-forget: load enabled modules from module registry
  fetchEnabledModules().catch(() => { /* ignore — all modules visible by default */ });

  // Fire-and-forget: refresh branch context from Gateway (Identity resolver may return stale data)
  if (response.user.id) {
    refreshBranchContext(response.user.id).catch(() => { /* ignore — login data used as fallback */ });
  }
}

async function fetchEnabledModules(): Promise<void> {
  const data = await get<Array<{ moduleName: string }>>('/modules/enabled');
  const names = data.map((m) => m.moduleName);
  useGlobalStore.getState().setEnabledModules(names);
}

export async function login(email: string, password: string): Promise<LoginResult> {
  const response = await post<AuthResponse>('/auth/login', {
    email,
    password,
  } as LoginRequest);

  if (response.accountLocked) {
    return { status: 'account_locked' };
  }

  // Both passwordExpired and normal success have full token data — cast is safe.
  type FullAuthResponse = AuthResponse & { accessToken: string; refreshToken: string; expiresAt: string; user: UserInfo };
  setAuthState(response as FullAuthResponse);

  if (response.passwordExpired) {
    return { status: 'password_expired' };
  }

  return { status: 'ok' };
}

export async function refreshSession(): Promise<boolean> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) return false;

  try {
    const response = await post<AuthResponse>('/auth/refresh', {
      refreshToken,
    });

    // Successful refresh always returns the full token+user payload; the
    // refresh endpoint returns 401 (caught below) when the token is invalid
    // or expired. Cast matches the login flow's FullAuthResponse pattern.
    type FullAuthResponse = AuthResponse & { accessToken: string; refreshToken: string; expiresAt: string; user: UserInfo };
    setAuthState(response as FullAuthResponse);
    return true;
  } catch {
    // Refresh token invalid or expired — clear and return false
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    return false;
  }
}

export async function logout(): Promise<void> {
  try {
    await post('/auth/logout');
  } catch {
    // Best-effort — server might be unreachable
  }

  localStorage.removeItem(REFRESH_TOKEN_KEY);
  useGlobalStore.getState().reset();
}

export async function getCurrentUser(): Promise<UserInfo | null> {
  try {
    return await get<UserInfo>('/auth/me');
  } catch {
    return null;
  }
}

export function hasStoredSession(): boolean {
  return !!localStorage.getItem(REFRESH_TOKEN_KEY);
}

export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<void> {
  await put('/account/password', { currentPassword, newPassword });
}

async function refreshBranchContext(userId: string): Promise<void> {
  const raw = await get<{
    currentBranch: { id: string; code: string; branchName: string; isHeadOffice: boolean } | null;
    primaryBranchId: string | null;
    effectiveBranches: Array<{ id: string; code: string; branchName: string; isHeadOffice: boolean }>;
    hasAllBranches: boolean;
  }>(`/admin/branch-assignments/users/${userId}/branch-context`);

  const context: BranchContext = {
    currentBranch: raw.currentBranch
      ? { id: raw.currentBranch.id, code: raw.currentBranch.code, name: raw.currentBranch.branchName, isHeadOffice: raw.currentBranch.isHeadOffice }
      : null,
    primaryBranchId: raw.primaryBranchId,
    effectiveBranches: raw.effectiveBranches.map(b => ({ id: b.id, code: b.code, name: b.branchName, isHeadOffice: b.isHeadOffice })),
    hasAllBranches: raw.hasAllBranches,
  };
  useGlobalStore.getState().setBranchContext(context);
}

export async function switchBranch(branchId: string): Promise<void> {
  const userId = useGlobalStore.getState().user?.id;
  if (!userId) throw new Error('Not authenticated');

  await patch(`/admin/branch-assignments/users/${userId}/current-branch`, { branchId });
  await refreshBranchContext(userId);
}
